import { useState } from "react";
import { Header } from "@/components/Header";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import { useTranslation } from "react-i18next";
import { BasicInfoStep } from "@/components/simplified-onboarding/BasicInfoStep";
import { ServiceCreationStep } from "@/components/simplified-onboarding/ServiceCreationStep";
import { signOut } from "@/lib/auth";

// Step type for the simplified onboarding process
type Step = 'basicInfo' | 'serviceCreation';

const SimpleOnboarding = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState<Step>('basicInfo');
  const [isLoading, setIsLoading] = useState(false);
  
  // Handle cancellation - logs out the user and redirects to auth page
  const handleCancel = async () => {
    setIsLoading(true);
    try {
      await signOut();
      
      toast({
        title: t('onboarding.onboardingCancelled'),
        description: t('onboarding.onboardingCancelledDescription'),
      });
      
      navigate("/auth", { replace: true });
    } catch (error) {
      console.error("Error during sign out:", error);
      
      // Force redirect even if signout fails
      navigate("/auth", { replace: true });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Move to the next step in onboarding
  const handleNext = () => {
    if (currentStep === 'basicInfo') {
      setCurrentStep('serviceCreation');
    } else if (currentStep === 'serviceCreation') {
      // Final step - navigate to dashboard services page
      navigate("/dashboard/services", { replace: true });
    }
  };
  
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="container max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-sm p-6 md:p-8">
          {currentStep === 'basicInfo' && (
            <BasicInfoStep 
              onCancel={handleCancel}
              onNext={handleNext}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
            />
          )}
          
          {currentStep === 'serviceCreation' && (
            <ServiceCreationStep
              onCancel={handleCancel}
              onComplete={handleNext}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default SimpleOnboarding; 